/**
 * UI Store - Single Source of Truth for All UI State
 *
 * Centralizes modal, panel, toolbar, and CSV UI state.
 * Replaces scattered useState in EditorPage.
 *
 * Store-First Pattern:
 * - All UI state lives here
 * - Components read via selectors
 * - Actions are the ONLY way to mutate state
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'

/**
 * CSV Upload View Modes
 */
export type ViewMode =
  | 'idle'
  | 'parsing'
  | 'parsed'
  | 'uploading'
  | 'uploaded'
  | 'source-select'

/**
 * Editor Toolbar Tools
 */
export type EditorTool = 'select' | 'pan' | 'zoom' | 'draw' | 'move' | null

/**
 * Modal State Interface
 */
export interface ModalState {
  save: boolean
  noTypes: boolean
  mapping: boolean
  error: boolean
  autoLink: boolean
  export: boolean
  saveSuccess: boolean
}

/**
 * Panel State Interface
 */
export interface PanelState {
  leftCollapsed: boolean
  rightCollapsed: boolean
  minimapVisible: boolean
}

/**
 * CSV UI State Interface
 */
export interface CSVUIState {
  viewMode: ViewMode
  showLayerModal: boolean
  selectedFileType: 'csv' | 'dxf' | null
  downloadError: string | null
  downloadMessage: string
}

/**
 * Editor UI State Interface
 */
export interface EditorUIState {
  modals: ModalState
  panels: PanelState
  toolbar: {
    selectedTool: EditorTool
  }
}

/**
 * Complete UI State
 */
export interface UIState {
  editor: EditorUIState
  csv: CSVUIState
}

/**
 * UI Actions Interface
 */
export interface UIActions {
  // Modal Actions
  openModal: (modal: keyof ModalState) => void
  closeModal: (modal: keyof ModalState) => void
  closeAllModals: () => void

  // Panel Actions
  toggleLeftPanel: () => void
  toggleRightPanel: () => void
  toggleMinimap: () => void
  setLeftPanel: (collapsed: boolean) => void
  setRightPanel: (collapsed: boolean) => void
  setMinimap: (visible: boolean) => void

  // Toolbar Actions
  setTool: (tool: EditorTool) => void

  // CSV UI Actions
  setCSVViewMode: (mode: ViewMode) => void
  setShowLayerModal: (show: boolean) => void
  setSelectedFileType: (type: 'csv' | 'dxf' | null) => void
  setDownloadError: (error: string | null) => void
  setDownloadMessage: (message: string) => void
  resetCSVUI: () => void
}

/**
 * Complete UI Store
 */
export type UIStore = UIState & UIActions

/**
 * Initial Modal State
 */
const initialModalState: ModalState = {
  save: false,
  noTypes: false,
  mapping: false,
  error: false,
  autoLink: false,
  export: false,
  saveSuccess: false
}

/**
 * Initial Panel State
 */
const initialPanelState: PanelState = {
  leftCollapsed: false,
  rightCollapsed: false,
  minimapVisible: true
}

/**
 * Initial CSV UI State
 */
const initialCSVUIState: CSVUIState = {
  viewMode: 'idle',
  showLayerModal: false,
  selectedFileType: null,
  downloadError: null,
  downloadMessage: 'Downloading file...'
}

/**
 * UI Store Implementation
 */
export const useUIStore = create<UIStore>()(
  devtools(
    (set) => ({
      // Initial State
      editor: {
        modals: initialModalState,
        panels: initialPanelState,
        toolbar: {
          selectedTool: null
        }
      },
      csv: initialCSVUIState,

      // Modal Actions
      openModal: (modal) =>
        set(
          (state) => ({
            editor: {
              ...state.editor,
              modals: {
                ...state.editor.modals,
                [modal]: true
              }
            }
          }),
          false,
          `ui/openModal/${modal}`
        ),

      closeModal: (modal) =>
        set(
          (state) => ({
            editor: {
              ...state.editor,
              modals: {
                ...state.editor.modals,
                [modal]: false
              }
            }
          }),
          false,
          `ui/closeModal/${modal}`
        ),

      closeAllModals: () =>
        set(
          (state) => ({
            editor: {
              ...state.editor,
              modals: initialModalState
            }
          }),
          false,
          'ui/closeAllModals'
        ),

      // Panel Actions
      toggleLeftPanel: () =>
        set(
          (state) => ({
            editor: {
              ...state.editor,
              panels: {
                ...state.editor.panels,
                leftCollapsed: !state.editor.panels.leftCollapsed
              }
            }
          }),
          false,
          'ui/toggleLeftPanel'
        ),

      toggleRightPanel: () =>
        set(
          (state) => ({
            editor: {
              ...state.editor,
              panels: {
                ...state.editor.panels,
                rightCollapsed: !state.editor.panels.rightCollapsed
              }
            }
          }),
          false,
          'ui/toggleRightPanel'
        ),

      toggleMinimap: () =>
        set(
          (state) => ({
            editor: {
              ...state.editor,
              panels: {
                ...state.editor.panels,
                minimapVisible: !state.editor.panels.minimapVisible
              }
            }
          }),
          false,
          'ui/toggleMinimap'
        ),

      setLeftPanel: (collapsed) =>
        set(
          (state) => ({
            editor: {
              ...state.editor,
              panels: {
                ...state.editor.panels,
                leftCollapsed: collapsed
              }
            }
          }),
          false,
          'ui/setLeftPanel'
        ),

      setRightPanel: (collapsed) =>
        set(
          (state) => ({
            editor: {
              ...state.editor,
              panels: {
                ...state.editor.panels,
                rightCollapsed: collapsed
              }
            }
          }),
          false,
          'ui/setRightPanel'
        ),

      setMinimap: (visible) =>
        set(
          (state) => ({
            editor: {
              ...state.editor,
              panels: {
                ...state.editor.panels,
                minimapVisible: visible
              }
            }
          }),
          false,
          'ui/setMinimap'
        ),

      // Toolbar Actions
      setTool: (tool) =>
        set(
          (state) => ({
            editor: {
              ...state.editor,
              toolbar: {
                selectedTool: tool
              }
            }
          }),
          false,
          'ui/setTool'
        ),

      // CSV UI Actions
      setCSVViewMode: (mode) =>
        set(
          (state) => ({
            csv: {
              ...state.csv,
              viewMode: mode
            }
          }),
          false,
          `ui/setCSVViewMode/${mode}`
        ),

      setShowLayerModal: (show) =>
        set(
          (state) => ({
            csv: {
              ...state.csv,
              showLayerModal: show
            }
          }),
          false,
          'ui/setShowLayerModal'
        ),

      setSelectedFileType: (type) =>
        set(
          (state) => ({
            csv: {
              ...state.csv,
              selectedFileType: type
            }
          }),
          false,
          `ui/setSelectedFileType/${type}`
        ),

      setDownloadError: (error) =>
        set(
          (state) => ({
            csv: {
              ...state.csv,
              downloadError: error
            }
          }),
          false,
          'ui/setDownloadError'
        ),

      setDownloadMessage: (message) =>
        set(
          (state) => ({
            csv: {
              ...state.csv,
              downloadMessage: message
            }
          }),
          false,
          'ui/setDownloadMessage'
        ),

      resetCSVUI: () =>
        set(
          {
            csv: initialCSVUIState
          },
          false,
          'ui/resetCSVUI'
        )
    }),
    { name: 'UIStore' }
  )
)

/**
 * Selectors for Optimized Component Re-renders
 */

// Modal Selectors
export const useModalState = () => useUIStore((state) => state.editor.modals)
export const useModal = (modal: keyof ModalState) =>
  useUIStore((state) => state.editor.modals[modal])

// Panel Selectors
export const usePanelState = () => useUIStore((state) => state.editor.panels)
export const useLeftPanelCollapsed = () =>
  useUIStore((state) => state.editor.panels.leftCollapsed)
export const useRightPanelCollapsed = () =>
  useUIStore((state) => state.editor.panels.rightCollapsed)
export const useMinimapVisible = () =>
  useUIStore((state) => state.editor.panels.minimapVisible)

// Toolbar Selectors
export const useSelectedTool = () =>
  useUIStore((state) => state.editor.toolbar.selectedTool)

// CSV UI Selectors
export const useCSVViewMode = () => useUIStore((state) => state.csv.viewMode)
export const useShowLayerModal = () => useUIStore((state) => state.csv.showLayerModal)
export const useSelectedFileType = () => useUIStore((state) => state.csv.selectedFileType)
export const useDownloadError = () => useUIStore((state) => state.csv.downloadError)
export const useDownloadMessage = () => useUIStore((state) => state.csv.downloadMessage)

// Action Selectors (useShallow prevents unnecessary re-renders)
export const useModalActions = () =>
  useUIStore(useShallow((state) => ({
    openModal: state.openModal,
    closeModal: state.closeModal,
    closeAllModals: state.closeAllModals
  })))

export const usePanelActions = () =>
  useUIStore(useShallow((state) => ({
    toggleLeftPanel: state.toggleLeftPanel,
    toggleRightPanel: state.toggleRightPanel,
    toggleMinimap: state.toggleMinimap,
    setLeftPanel: state.setLeftPanel,
    setRightPanel: state.setRightPanel,
    setMinimap: state.setMinimap
  })))

export const useToolbarActions = () =>
  useUIStore(useShallow((state) => ({
    setTool: state.setTool
  })))

export const useCSVUIActions = () =>
  useUIStore(useShallow((state) => ({
    setCSVViewMode: state.setCSVViewMode,
    setShowLayerModal: state.setShowLayerModal,
    setSelectedFileType: state.setSelectedFileType,
    setDownloadError: state.setDownloadError,
    setDownloadMessage: state.setDownloadMessage,
    resetCSVUI: state.resetCSVUI
  })))
